import React, {
  useCallback, useEffect, useReducer, useRef,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import axios from 'axios';
import { Alert, Button, Spin } from 'antd';
import csrfHeaders from '../../utils/csrfHeaders';
import './appointmentHistory.scss';

// The selected patient's past visits (date, provider, location), past year
// first, from one of the endpoints in appointmentHistorySources.js. Read-only,
// and never blocks the form.
const LOAD_ERROR = 'Could not load appointment history. Please try again.';
const NOT_AVAILABLE = 'Not available';

const initialState = {
  appointments: [],
  nextOffset: 0,
  earlierAvailable: false,
  loading: null, // 'recent' | 'earlier'
  loaded: false,
  loadedEarlier: false,
  error: null, // { message, mode }
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'reset':
      return initialState;
    case 'started':
      return { ...state, loading: action.mode, error: null };
    case 'succeeded':
      return {
        ...state,
        appointments: action.mode === 'earlier' ? [...state.appointments, ...action.appointments] : action.appointments,
        nextOffset: action.nextOffset,
        earlierAvailable: action.earlierAvailable,
        loading: null,
        loaded: true,
        loadedEarlier: state.loadedEarlier || action.mode === 'earlier',
      };
    case 'failed':
      return { ...state, loading: null, error: { message: action.message, mode: action.mode } };
    default:
      return state;
  }
};

const cancelledRequest = (error, controller) => (
  controller.signal.aborted || axios.isCancel(error) || error?.name === 'CanceledError'
);

// A 422 carries an actionable message (an expired patient selection, an
// unavailable practice); anything else gets the generic retry copy.
const errorMessage = (error) => {
  const detail = error?.response?.data?.error;
  return error?.response?.status === 422 && detail ? detail : LOAD_ERROR;
};

const formatDate = (value) => {
  const date = moment(value, 'YYYY-MM-DD', true);
  return date.isValid() ? date.format('MMM D, YYYY') : value;
};

const AppointmentHistory = ({
  searchPath, visitNoun, credentialId, location, patientIdentifier, selectionProof,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const requestRef = useRef(null);

  // The first page sends no offset; "Show earlier" always sends one, and 0 is
  // a valid place to resume when no visit is from the past year.
  const load = useCallback((mode, offset) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    dispatch({ type: 'started', mode });

    axios.post(searchPath, {
      emr_integration_credential_id: credentialId,
      location,
      patient_identifier: patientIdentifier,
      patient_selection_proof: selectionProof,
      ...(mode === 'earlier' && { offset }),
    }, { headers: csrfHeaders(), signal: controller.signal }).then((response) => {
      if (controller.signal.aborted) return;
      const data = response.data || {};
      dispatch({
        type: 'succeeded',
        mode,
        appointments: Array.isArray(data.appointments) ? data.appointments : [],
        nextOffset: Number.isInteger(data.next_offset) ? data.next_offset : (offset ?? 0),
        earlierAvailable: data.earlier_available === true,
      });
    }).catch((error) => {
      if (cancelledRequest(error, controller)) return;
      dispatch({ type: 'failed', mode, message: errorMessage(error) });
    });
  }, [searchPath, credentialId, location, patientIdentifier, selectionProof]);

  // NextGen also needs the practice (location); the drawer only renders this
  // once it is picked. eCW has no practice.
  useEffect(() => {
    dispatch({ type: 'reset' });
    if (!credentialId || !patientIdentifier || !selectionProof) return undefined;

    load('recent');
    return () => requestRef.current?.abort();
  }, [credentialId, location, patientIdentifier, selectionProof, load]);

  const {
    appointments, nextOffset, earlierAvailable, loading, loaded, loadedEarlier, error,
  } = state;

  const handleRetry = () => {
    if (error?.mode === 'earlier') {
      load('earlier', nextOffset);
    } else {
      load('recent');
    }
  };

  // After "Show earlier" reaches the end of the list, say so: the button
  // disappearing on its own reads as nothing having happened.
  let emptyMessage = null;
  if (loaded && !loading && !error) {
    if (loadedEarlier && !earlierAvailable) {
      emptyMessage = `No earlier ${visitNoun}.`;
    } else if (appointments.length === 0) {
      emptyMessage = `No ${visitNoun} in the past year.`;
    }
  }

  return (
    <div className="appointment-history" aria-busy={Boolean(loading)}>
      {loading === 'recent' && (
        <Spin size="small" tip="Loading appointment history..." />
      )}
      {appointments.length > 0 && (
        <table className="appointment-history__table">
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Provider</th>
              <th scope="col">Location</th>
            </tr>
          </thead>
          <tbody>
            {appointments.map((appointment, index) => (
              // Visits carry no ids by design; the list only ever grows at the end.
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${appointment.date}-${index}`}>
                <td className="appointment-history__date">{formatDate(appointment.date)}</td>
                <td className={appointment.provider ? undefined : 'appointment-history__missing'}>
                  {appointment.provider || NOT_AVAILABLE}
                </td>
                <td className={appointment.location ? undefined : 'appointment-history__missing'}>
                  {appointment.location || NOT_AVAILABLE}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {emptyMessage && (
        <div className="appointment-history__empty" role="status">{emptyMessage}</div>
      )}
      {error && (
        <Alert
          type="warning"
          showIcon
          message={error.message}
          action={<Button size="small" onClick={handleRetry}>Retry</Button>}
        />
      )}
      {loading === 'earlier' && <Spin size="small" tip="Loading earlier appointments..." />}
      {loaded && !loading && !error && earlierAvailable && (
        <Button
          type="link"
          className="appointment-history__earlier"
          onClick={() => load('earlier', nextOffset)}
        >
          Show earlier
        </Button>
      )}
    </div>
  );
};

AppointmentHistory.propTypes = {
  searchPath: PropTypes.string.isRequired,
  // Plural, as in "No kept appointments in the past year."
  visitNoun: PropTypes.string.isRequired,
  credentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  location: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  patientIdentifier: PropTypes.string,
  selectionProof: PropTypes.string,
};

AppointmentHistory.defaultProps = {
  credentialId: undefined,
  location: undefined,
  patientIdentifier: undefined,
  selectionProof: undefined,
};

export default AppointmentHistory;
